// src/components/apps/LatestApps.jsx
import React, { useState } from 'react';
import './latest-apps.css';

const LatestApps = () => {
  const [hoveredApp, setHoveredApp] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);

  const apps = [
    {
      id: 1,
      name: 'TaskFlow Pro',
      tagline: 'Productivity Redefined',
      description: 'Streamline your workflow with intelligent task management.',
      icon: 'https://picsum.photos/120/120?random=1',
      mockup: 'https://picsum.photos/300/600?random=10',
      comingSoon: true,
      launchDate: 'December 2026',
      features: [
        { icon: '🚀', title: 'Smart Scheduling', desc: 'AI-powered task organization' },
        { icon: '👥', title: 'Team Sync', desc: 'Real-time collaboration' },
        { icon: '📊', title: 'Analytics', desc: 'Performance insights' },
        { icon: '🔄', title: 'Automation', desc: 'Workflow automation' }
      ],
      stats: {
        downloads: '0+',
        rating: 5.0,
        size: '48MB'
      },
      primaryColor: '#667eea',
      secondaryColor: '#764ba2',
      appStoreUrl: 'https://apps.apple.com/app/taskflow-pro'
    },
    {
      id: 2,
      name: 'FitTrack AI',
      tagline: 'Your AI Fitness Coach',
      description: 'Transform your fitness journey with personalized AI coaching.',
      icon: 'https://picsum.photos/120/120?random=2',
      mockup: 'https://picsum.photos/300/600?random=20',
      comingSoon: true,
      launchDate: 'December 2026',
      features: [
        { icon: '💪', title: 'AI Workouts', desc: 'Personalized training plans' },
        { icon: '🥗', title: 'Nutrition', desc: 'Smart meal tracking' },
        { icon: '📈', title: 'Progress', desc: 'Visual progress tracking' },
        { icon: '⌚', title: 'Wearables', desc: 'Device integration' }
      ],
      stats: {
        downloads: '0+',
        rating: 5.0,
        size: '62MB'
      },
      primaryColor: '#06ffa5',
      secondaryColor: '#00b4d8',
      appStoreUrl: 'https://apps.apple.com/app/fittrack-ai'
    }
  ];

  return (
    <section className="latest-apps section" id="portfolio">
      <div className="apps__header">
        {/* <h2 className="section__title">Latest Apps</h2> */}
        <span className="section__subtitle">Crafted with passion, powered by innovation</span>
      </div>

      <div className="apps__showcase container">
        {apps.map((app, index) => (
          <div
            key={app.id}
            className={`app__card ${hoveredApp === index ? 'hovered' : ''}`}
            onMouseEnter={() => setHoveredApp(index)}
            onMouseLeave={() => setHoveredApp(null)}
            style={{
              '--primary': app.primaryColor,
              '--secondary': app.secondaryColor
            }}
          >
            {/* Floating Elements */}
            <div className="floating-elements">
              <div className="float-1"></div>
              <div className="float-2"></div>
              <div className="float-3"></div>
            </div>

            {/* Coming Soon banner — prominent, top of card */}
            {app.comingSoon && (
              <div className="app__coming-soon">
                <span className="coming-soon-pill">
                  <i className='bx bx-rocket'></i> Coming Soon
                </span>
                <span className="launch-date">
                  <i className='bx bx-calendar'></i> Launching {app.launchDate}
                </span>
              </div>
            )}

            {/* App Header */}
            <div className="app__card-header">
              <div className="app__identity">
                <div className="app__icon-wrapper">
                  <img
                    src={app.icon}
                    alt={app.name}
                    className="app__icon"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span>${app.name.substring(0, 2)}</span>`;
                    }}
                  />
                </div>
                <div>
                  <h3 className="app__name">{app.name}</h3>
                  <p className="app__tagline">{app.tagline}</p>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="app__quick-stats">
                <div className="stat">
                  <span className="stat-value">{app.stats.downloads}</span>
                  <span className="stat-label">Downloads</span>
                </div>
                <div className="stat">
                  <span className="stat-value">
                    <i className='bx bxs-star'></i> {app.stats.rating.toFixed(1)}
                  </span>
                  <span className="stat-label">Rating</span>
                </div>
              </div>
            </div>

            {/* Description */}
            <p className="app__description">{app.description}</p>

            {/* Interactive Features Grid */}
            <div className="app__features-grid">
              {app.features.map((feature, idx) => (
                <div
                  key={idx}
                  className={`feature-tile ${selectedFeature === `${index}-${idx}` ? 'active' : ''}`}
                  onClick={() => setSelectedFeature(`${index}-${idx}`)}
                >
                  <span className="feature-icon">{feature.icon}</span>
                  <h4>{feature.title}</h4>
                  <p>{feature.desc}</p>
                </div>
              ))}
            </div>

            {/* Phone Mockup */}
            <div className="app__mockup">
              <div className="phone-frame">
                <div className="phone-screen">
                  <img
                    src={app.mockup}
                    alt={`${app.name} mockup`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.style.background = `linear-gradient(135deg, ${app.primaryColor}, ${app.secondaryColor})`;
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Download CTA */}
            <div className="app__cta">
              {app.comingSoon ? (
                <button
                  type="button"
                  className="download-button download-button--soon"
                  disabled
                  aria-disabled="true"
                >
                  <i className='bx bx-lock-alt'></i>
                  <span>Download Not Available Yet</span>
                </button>
              ) : (
                <a
                  href={app.appStoreUrl}
                  className="download-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className='bx bxl-apple'></i>
                  <span>Download on App Store</span>
                </a>
              )}
              <span className="app-size">{app.stats.size}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Background Decoration */}
      <div className="apps__bg-decoration">
        <div className="decoration-circle-1"></div>
        <div className="decoration-circle-2"></div>
      </div>
    </section>
  );
};

export default LatestApps;